import { getCornerstone, getRenderingEngine as getRenderingEngineClass } from "./dicomLoader";
import { emit as viewerBusEmit } from "../lib/viewerBus";
import { logger } from "./logger";

// Single rendering engine shared across all viewports in the app — same
// pattern as OHIF's CornerstoneViewportService. Per-viewport engines hit the
// browser's WebGL context cap (~8-16) on multi-viewport layouts; one engine
// lets Cornerstone batch renders and share GPU textures across viewports
// showing the same series.
export const SHARED_RENDERING_ENGINE_ID = "viewerSharedEngine";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RenderingEngine = any;

// Cornerstone's WebGL canvases live inside offscreen <div> containers owned
// by the engine's internal implementation. There is no public accessor; we
// duck-type across the two engine modes and best-effort attach. Property
// names verified against @cornerstonejs/core in this repo's node_modules:
//   - public `RenderingEngine` wraps either `TiledRenderingEngine` or
//     `ContextPoolRenderingEngine` under `_implementation`
//   - tiled mode exposes `offScreenCanvasContainer` (a <div> with the vtk
//     canvas as a child)
//   - context-pool mode exposes `contextPool.offScreenCanvasContainers`
//     (an array of <div>s, one per pooled context)
// Both `setContainer()` paths in vtk synchronously append the <canvas> into
// the div, so probing immediately after engine construction works. If a
// future @cornerstonejs/core bump renames these fields the probe returns
// empty and the warn below fires once per engine creation — see the unit
// test in renderingEngine.test.ts that pins the contract.
export const collectOffscreenGLCanvases = (engine: RenderingEngine): HTMLCanvasElement[] => {
  const canvases: HTMLCanvasElement[] = [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const impl = (engine as any)._implementation ?? engine;
  const tiledContainer = impl?.offScreenCanvasContainer;
  if (tiledContainer instanceof HTMLElement) {
    const c = tiledContainer.querySelector("canvas");
    if (c instanceof HTMLCanvasElement) canvases.push(c);
  }
  const poolContainers = impl?.contextPool?.offScreenCanvasContainers;
  if (Array.isArray(poolContainers)) {
    for (const container of poolContainers) {
      if (container instanceof HTMLElement) {
        const c = container.querySelector("canvas");
        if (c instanceof HTMLCanvasElement) canvases.push(c);
      }
    }
  }
  return canvases;
};

const attachContextLossListeners = (engine: RenderingEngine): void => {
  const canvases = collectOffscreenGLCanvases(engine);
  if (canvases.length === 0) {
    logger.warn(
      "[renderingEngine] viewer_webgl_canvas_probe_empty engine=%s",
      SHARED_RENDERING_ENGINE_ID,
    );
    return;
  }
  for (const canvas of canvases) {
    // Recoverable context loss. We do NOT destroy the engine here: it is an
    // app-lifetime singleton, and the loss is usually the browser reclaiming the
    // OLDEST of too-many contexts — destroying + recreating the engine only mints
    // a fresh context pool and makes the exhaustion worse. `preventDefault`
    // requests restoration (vtk.js also does this on its own listener; calling it
    // here is harmless and explicit). On `webglcontextlost` we surface a
    // TRANSIENT "restoring" state; on `webglcontextrestored` vtk rebuilds the
    // render window and we tell viewports to re-attach their volumes/stacks (the
    // GPU textures were dropped with the context). NOT `once` — a long session
    // may lose + restore more than once.
    const onContextLost = (evt: Event) => {
      evt.preventDefault();
      logger.error("viewer_webgl_context_lost engine=%s", SHARED_RENDERING_ENGINE_ID);
      viewerBusEmit("webglContextLost", {});
    };
    const onContextRestored = () => {
      logger.warn(
        "[renderingEngine] viewer_webgl_context_restored engine=%s",
        SHARED_RENDERING_ENGINE_ID,
      );
      viewerBusEmit("webglContextRestored", {});
    };
    canvas.addEventListener("webglcontextlost", onContextLost);
    canvas.addEventListener("webglcontextrestored", onContextRestored);
  }
};

export const getOrCreateSharedRenderingEngine = (): RenderingEngine => {
  const cornerstone = getCornerstone();
  const existing = cornerstone.getRenderingEngine(SHARED_RENDERING_ENGINE_ID);
  if (existing && !existing.hasBeenDestroyed) {
    return existing;
  }
  const RenderingEngineClass = getRenderingEngineClass();
  const engine = new RenderingEngineClass(SHARED_RENDERING_ENGINE_ID);
  attachContextLossListeners(engine);
  return engine;
};

// Disable a viewport on the shared engine. The engine is deliberately NEVER
// destroyed here: it is an app-lifetime singleton (its WebGL context pool is
// meant to be reused, OHIF-style). The previous "destroy when the last viewport
// unmounts" behavior fired on every case switch in the reader viewer — the
// series picker unmounts the whole grid, dropping viewport count to zero — so
// the engine was destroyed and recreated per case. Each recreation allocated a
// fresh pool of ~11 WebGL contexts while the browser reclaimed the old pools too
// slowly, blowing past the ~16-context cap → "Too many active WebGL contexts" →
// "Viewer lost its GPU context. Reload the page." Keeping the engine alive across
// case switches keeps the context count bounded. `disableElement` still runs so
// the departing viewport's context returns to the pool for reuse. Engine teardown
// belongs to app teardown (dicomLoader `cleanup`).
export const disableSharedViewport = (viewportId: string): void => {
  try {
    const cornerstone = getCornerstone();
    const engine = cornerstone.getRenderingEngine(SHARED_RENDERING_ENGINE_ID);
    if (!engine || engine.hasBeenDestroyed) return;
    try {
      engine.disableElement(viewportId);
    } catch {
      /* element already disabled */
    }
  } catch {
    /* cornerstone not initialized */
  }
};
